import type {
  DeviceEntry,
  FiguresData,
  MapDestination,
  MapTransit,
  ShipmentSummary,
} from "../types";

export type DatasetDateRange = {
  start: string;
  end: string;
};

type TimeBounds = {
  start: number;
  end: number;
};

export function getDatasetDateBounds(shipments: ShipmentSummary[]): DatasetDateRange | null {
  const timestamps: number[] = [];

  for (const shipment of shipments) {
    const departure = getShipmentDepartureTimestamp(shipment);
    if (departure !== null) timestamps.push(departure);
  }

  if (!timestamps.length) return null;

  return {
    start: toDateInputValue(Math.min(...timestamps)),
    end: toDateInputValue(Math.max(...timestamps)),
  };
}

export function filterShipmentsByDateRange(
  shipments: ShipmentSummary[],
  range: DatasetDateRange | null,
): ShipmentSummary[] {
  const selectedBounds = rangeToTimeBounds(range);
  if (!selectedBounds) return shipments;

  return shipments.filter((shipment) => {
    const departure = getShipmentDepartureTimestamp(shipment);
    if (departure === null) return false;
    return departure >= selectedBounds.start && departure <= selectedBounds.end;
  });
}

export function getShipmentCountries(shipments: ShipmentSummary[]): string[] {
  const countries = new Set<string>();
  for (const shipment of shipments) {
    if (shipment.destinationCountry) countries.add(shipment.destinationCountry);
    shipment.countries.forEach((country) => {
      if (country) countries.add(country);
    });
  }
  return Array.from(countries).sort((a, b) => a.localeCompare(b));
}

export function filterMapDestinations(
  destinations: MapDestination[],
  shipments: ShipmentSummary[],
  safeRange: { min: number; max: number },
): MapDestination[] {
  const shipmentById = new Map(shipments.map((shipment) => [shipment.id, shipment]));

  return destinations.flatMap((destination) => {
    const consignmentIds = destination.consignmentIds.filter((id) => shipmentById.has(id));
    if (!consignmentIds.length) return [];

    const matchingShipments = consignmentIds
      .map((id) => shipmentById.get(id))
      .filter((shipment): shipment is ShipmentSummary => Boolean(shipment));
    const devices = matchingShipments.flatMap((shipment) => shipment.devices);
    const compliance = countCompliance(devices, matchingShipments);
    const temperature = countTemperatureStates(devices, safeRange);
    const ratio = consignmentIds.length / Math.max(destination.consignmentIds.length, 1);

    return [{
      ...destination,
      consignmentIds,
      counts: compliance,
      lowOkHigh: temperature,
      expedition: scaleAggregate(destination.expedition, ratio),
      recovered: scaleAggregate(destination.recovered, ratio),
    }];
  });
}

export function filterMapTransits(transits: MapTransit[] | undefined, shipments: ShipmentSummary[]): MapTransit[] {
  if (!transits) return [];
  const shipmentIds = new Set(shipments.map((shipment) => shipment.id));

  return transits.flatMap((transit) => {
    const consignmentIds = transit.consignmentIds.filter((id) => shipmentIds.has(id));
    if (!consignmentIds.length) return [];
    return [{ ...transit, consignmentIds, count: consignmentIds.length }];
  });
}

export function buildFiguresForShipments(
  shipments: ShipmentSummary[],
  destinations: MapDestination[] = [],
): FiguresData {
  const devices = shipments.flatMap((shipment) => shipment.devices);
  const insideDevices = devices.filter((device) => normalizePosition(device.position) === "INSIDE");
  const outsideDevices = devices.filter((device) => normalizePosition(device.position) === "OUTSIDE");
  const timeline = countBy(shipments, shipmentDateKey);
  const countries = countBy(shipments, (shipment) => shipment.destinationCountry ?? "Inconnu");
  const boxes = buildBoxStatistics(shipments);
  const durationValues = shipments
    .map((shipment) => shipment.totalHours)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value) && value >= 0);
  const eventDates = shipments
    .flatMap((shipment) => [shipment.firstEvent, shipment.lastEvent])
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => a.localeCompare(b));

  return {
    complianceInside: complianceCounts(insideDevices),
    complianceOutside: complianceCounts(outsideDevices),
    recovery: {
      shipped: destinations.reduce((sum, destination) => sum + destination.expedition, 0),
      recovered: destinations.reduce((sum, destination) => sum + destination.recovered, 0),
    },
    heatmapInside: [],
    heatmapOutside: [],
    totals: {
      totalShipments: shipments.length,
      totalEvents: shipments.reduce((sum, shipment) => sum + shipment.eventCount, 0),
      totalCountries: countries.size,
      firstEvent: eventDates[0] ?? null,
      lastEvent: eventDates[eventDates.length - 1] ?? null,
    },
    timeline: Array.from(timeline.entries()).map(([date, count]) => ({ date, shipments: count })),
    countries: Array.from(countries.entries()).map(([country, count]) => ({ country, shipments: count }))
      .sort((a, b) => b.shipments - a.shipments)
      .slice(0, 8),
    boxDistribution: Array.from(boxes.entries()).map(([boxIdentifier, stats]) => ({
      boxIdentifier,
      shipments: stats.shipments,
    })),
    boxExcursionRate: Array.from(boxes.entries()).map(([boxIdentifier, stats]) => ({
      boxIdentifier,
      shipments: stats.shipments,
      withExcursion: stats.withExcursion,
      excursionRate: stats.shipments > 0 ? (stats.withExcursion / stats.shipments) * 100 : 0,
    })),
    histogramHours: buildDurationHistogram(durationValues),
    destinationExperience: buildDestinationExperience(shipments),
    outsideTemperature: buildOutsideTemperature(shipments),
  };
}

function addTimestamp(target: number[], value: string | null) {
  if (!value) return;
  const timestamp = Date.parse(value);
  if (Number.isFinite(timestamp)) target.push(timestamp);
}

function toDateInputValue(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

function rangeToTimeBounds(range: DatasetDateRange | null): TimeBounds | null {
  if (!range) return null;
  const start = Date.parse(`${range.start}T00:00:00.000Z`);
  const end = Date.parse(`${range.end}T23:59:59.999Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return null;
  return { start, end };
}

function getTimeBounds(values: Array<string | null>): TimeBounds | null {
  const timestamps: number[] = [];
  values.forEach((value) => addTimestamp(timestamps, value));
  if (!timestamps.length) return null;
  return { start: Math.min(...timestamps), end: Math.max(...timestamps) };
}

function getShipmentDepartureTimestamp(shipment: ShipmentSummary): number | null {
  const deviceStarts = getTimeBounds(shipment.devices.map((device) => device.timeBegin));
  if (deviceStarts) return deviceStarts.start;

  const eventStarts = getTimeBounds([shipment.firstEvent, ...shipment.timeline]);
  return eventStarts?.start ?? null;
}

function countCompliance(devices: DeviceEntry[], shipments: ShipmentSummary[]) {
  if (!devices.length) {
    return shipments.reduce(
      (counts, shipment) => {
        if (shipment.compliance === "YES") counts.compliant += 1;
        if (shipment.compliance === "NO") counts.nonCompliant += 1;
        return counts;
      },
      { compliant: 0, nonCompliant: 0 },
    );
  }

  return devices.reduce(
    (counts, device) => {
      if (hasExcursion(device)) counts.nonCompliant += 1;
      if (!hasExcursion(device)) counts.compliant += 1;
      return counts;
    },
    { compliant: 0, nonCompliant: 0 },
  );
}

function countTemperatureStates(devices: DeviceEntry[], safeRange: { min: number; max: number }) {
  return devices.reduce(
    (counts, device) => {
      const isLow = typeof device.minValue === "number" && device.minValue < safeRange.min;
      const isHigh = typeof device.maxValue === "number" && device.maxValue > safeRange.max;
      if (isLow) counts.low += 1;
      if (isHigh) counts.high += 1;
      if (!isLow && !isHigh) counts.ok += 1;
      return counts;
    },
    { low: 0, ok: 0, high: 0 },
  );
}

function scaleAggregate(value: number, ratio: number): number {
  return Number((value * ratio).toFixed(2));
}

function normalizePosition(position: string | null): string {
  return (position ?? "").trim().toUpperCase();
}

function complianceCounts(devices: DeviceEntry[]) {
  let yes = 0;
  let no = 0;
  for (const device of devices) {
    if (hasExcursion(device)) {
      no += 1;
    } else {
      yes += 1;
    }
  }
  return { yes, no };
}

function hasExcursion(device: DeviceEntry): boolean {
  return typeof device.totalExcursionHours === "number" && device.totalExcursionHours > 0;
}

function shipmentDateKey(shipment: ShipmentSummary): string | null {
  const departure = getShipmentDepartureTimestamp(shipment);
  if (departure === null) return null;
  return toDateInputValue(departure);
}

function countBy(
  shipments: ShipmentSummary[],
  getKey: (shipment: ShipmentSummary) => string | null,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const shipment of shipments) {
    const key = getKey(shipment);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function buildBoxStatistics(shipments: ShipmentSummary[]) {
  const stats = new Map<string, { shipments: number; withExcursion: number }>();
  for (const shipment of shipments) {
    const shipmentBoxes = splitBoxIdentifiers(shipment.boxIdentifier);
    let hasDevicePackage = false;

    for (const device of shipment.devices) {
      let deviceBoxes = splitBoxIdentifiers(device.boxIdentifier);
      if (!deviceBoxes.length && shipmentBoxes.length === 1) {
        deviceBoxes = shipmentBoxes;
      }
      if (!deviceBoxes.length) continue;

      hasDevicePackage = true;
      for (const box of deviceBoxes) {
        registerBoxUsage(stats, box, hasExcursion(device));
      }
    }

    if (hasDevicePackage) continue;
    const shipmentHasExcursion =
      typeof shipment.totalExcursionHours === "number" && shipment.totalExcursionHours > 0;
    for (const box of shipmentBoxes) {
      registerBoxUsage(stats, box, shipmentHasExcursion);
    }
  }
  return stats;
}

function splitBoxIdentifiers(value: string | null): string[] {
  if (!value) return [];
  const identifiers = new Set<string>();
  for (const token of value.split(/[;,]/)) {
    const identifier = token.trim();
    if (identifier) identifiers.add(identifier);
  }
  return Array.from(identifiers);
}

function registerBoxUsage(
  stats: Map<string, { shipments: number; withExcursion: number }>,
  boxIdentifier: string,
  withExcursion: boolean,
) {
  const current = stats.get(boxIdentifier) ?? { shipments: 0, withExcursion: 0 };
  current.shipments += 1;
  if (withExcursion) current.withExcursion += 1;
  stats.set(boxIdentifier, current);
}

function buildDurationHistogram(values: number[]) {
  if (!values.length) return [];
  const max = Math.max(...values);
  let step = 24;
  if (max <= 24) step = 2;
  else if (max <= 72) step = 4;
  else if (max <= 168) step = 8;
  else if (max <= 336) step = 12;

  const buckets: Array<{ range: string; count: number }> = [];
  for (let start = 0; start <= max; start += step) {
    const end = start + step;
    const count = values.filter((value) => value >= start && value < end).length;
    buckets.push({ range: `${start}-${end}`, count });
  }
  return buckets;
}

function buildDestinationExperience(shipments: ShipmentSummary[]) {
  const valuesByDestination = new Map<string, number[]>();
  for (const shipment of shipments) {
    if (typeof shipment.totalHours !== "number") continue;
    const destination = shipment.destinationCountry ?? shipment.destinationCity ?? "Inconnu";
    const values = valuesByDestination.get(destination) ?? [];
    values.push(shipment.totalHours);
    valuesByDestination.set(destination, values);
  }
  return Array.from(valuesByDestination.entries()).map(([destination, values]) => ({
    destination,
    min: Math.min(...values),
    max: Math.max(...values),
    mean: values.reduce((sum, value) => sum + value, 0) / values.length,
  }));
}

function buildOutsideTemperature(shipments: ShipmentSummary[]) {
  const valuesByDestination = new Map<string, Array<{ min: number; max: number }>>();
  for (const shipment of shipments) {
    const destination = shipment.destinationCountry ?? shipment.destinationCity ?? "Inconnu";
    for (const device of shipment.devices) {
      if (normalizePosition(device.position) !== "OUTSIDE") continue;
      if (typeof device.minValue !== "number" || typeof device.maxValue !== "number") continue;
      const values = valuesByDestination.get(destination) ?? [];
      values.push({ min: device.minValue, max: device.maxValue });
      valuesByDestination.set(destination, values);
    }
  }
  return Array.from(valuesByDestination.entries()).map(([destination, values]) => ({
    destination,
    min: Math.min(...values.map((value) => value.min)),
    max: Math.max(...values.map((value) => value.max)),
    mean: values.reduce((sum, value) => sum + ((value.min + value.max) / 2), 0) / values.length,
  }));
}
